import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/App";
import { ShieldAlert, LogOut, Loader2 } from "lucide-react";

/**
 * Persistent top banner shown across the app while an admin is impersonating
 * an end-user (customer-support flow). Sticky, full-width, eye-catching but
 * not destructive. Single CTA: exit impersonation → reload to admin view.
 *
 * Visible only when AuthContext.impersonation.active === true.
 */
export default function ImpersonationBanner() {
  const { user, impersonation } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!impersonation?.active) return null;

  const exit = async () => {
    setExiting(true);
    try {
      await axios.post(`${API}/admin/impersonation/exit`, {}, { withCredentials: true });
      toast.success(`Exited impersonation — back to ${impersonation.admin_email || "admin"}`);
      // Hard reload so all in-flight queries re-fetch with the admin session.
      window.location.href = "/admin/users";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't exit impersonation");
      setExiting(false);
    }
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-amber-950 shadow-md"
      data-testid="impersonation-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm font-semibold truncate">
            Viewing as <span className="underline underline-offset-2">{user?.email}</span>
            <span className="font-normal text-amber-900/80 ml-2 hidden sm:inline">
              · admin: {impersonation.admin_email}
            </span>
          </p>
        </div>
        <button
          onClick={exit}
          disabled={exiting}
          data-testid="exit-impersonation-btn"
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-950 text-amber-50 text-xs font-semibold hover:bg-black transition-colors disabled:opacity-60"
        >
          {exiting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
          Exit impersonation
        </button>
      </div>
    </div>
  );
}
